import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {SponsorshipTierData} from "types/SponsorshipTier";
import SponsorshipTierForm from "pages/SponsorshipTier/Form";
import {FormikHelpers} from "formik";
import {useOperator} from "context/OperatorContext";

function MutateSponsorshipTier() {
    const {id} = useParams()
    const {data: sponsorshipTier, isLoading} = useQuery(
        ['sponsorship-tiers', {id}],
        () => ngage.sponsorshipTiers.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: SponsorshipTierData) => ngage.sponsorshipTiers.store(data))
    const update = useMutation((data: SponsorshipTierData) => ngage.sponsorshipTiers.update(parseInt(id as string), data))

    const {termFor} = useOperator()
    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: SponsorshipTierData, {setSubmitting}: FormikHelpers<SponsorshipTierData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('sponsorship-tiers.all')

        setSubmitting(false)
        navigate('/sponsorship-tiers')
    }

    // Handle store mutation
    async function handleStore(values: SponsorshipTierData) {
        try {
            await store.mutateAsync(values)
            notify("success", `${termFor('Sponsorship')} Tier Created`, `${values.name} has been created`)
        } catch (error) {
            notify("error", `${termFor('Sponsorship')} Tier Not Created`, `${values.name} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: SponsorshipTierData) {
        try {
            await update.mutateAsync(values)
            notify("success", `${termFor('Sponsorship')} Tier Updated`, `${values.name} has been updated`)
            await invalidate(['sponsorship-tiers', {id}])
        } catch (error) {
            notify("error", `${termFor('Sponsorship')} Tier Not Updated`, `${values.name} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {
        'Commercial': '/',
        [`${termFor('Sponsorship')} Tiers`]: '/sponsorship-tiers',
        [`New ${termFor('Sponsorship')} Tier`]: '/sponsorship-tiers/new'
    }

    const editBreadcrumbs = {
        'Commercial': '/',
        [`${termFor('Sponsorship')} Tiers`]: '/sponsorship-tiers',
        [`Edit ${termFor('Sponsorship')} Tier`]: `/sponsorship-tiers/${id}/edit`
    }

    return (
        <Page title={`${id? 'Edit' : 'New'} ${termFor('Sponsorship')} Tier`}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <SponsorshipTierForm sponsorshipTier={sponsorshipTier} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateSponsorshipTier;